import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandDefaultCurrency } from '../../shared/utils/brandCurrency';
import {
  formatComptabiliteDate,
  formatComptabiliteMoney,
  getComptabiliteDateLocale,
  getComplianceFrameworkKey,
  getIncomeTaxLineKey } from './utils/comptabiliteSaudiFormat';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle, AlertCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Layers, ShoppingCart, CreditCard, Target, List, LayoutList, TrendingDown, Award, Percent, BarChart3, FileText } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
} from '../../products/saudi/components/QawaemTableSelection';
import { useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  getResultats,
  createResultat,
  updateResultat,
  deleteResultat,
} from '../../shared/utils/comptabiliteService';
import { fetchCurrentFiscalYearContext, resolveFiscalYearLabel } from './utils/fiscalYearUtils';
import ComptabiliteFiscalYearField from './components/ComptabiliteFiscalYearField';
import { CustomDatePicker, CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import {
  EMPTY_COMPTE_RESULTAT_FORM,
  buildCompteResultatPayload,
  recordToFormData } from './07-compte-resultat/compteResultatPayload';

const STATUT_FILTER_VALUES = {
  provisoire: 'Provisoire',
  definitif: 'Définitif',
  valide: 'Validé' };

const RESULTAT_LIST_COLUMNS = [
  { key: 'resultat_ref', labelKey: 'col_reference' },
  { key: 'exercice_fiscal', labelKey: 'col_exercice_fiscal' },
  { key: 'periode', labelKey: 'col_period' },
  { key: 'chiffre_affaires', labelKey: 'col_chiffre_affaires' },
  { key: 'resultat_net', labelKey: 'col_resultat_net' },
  { key: 'statut_resultat', labelKey: 'col_status' },
];

const CompteResultatSituationPage = () => {
  const { t, i18n } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const p = (key, opts) => t(`modules.comptabilite.pages.compteResultatSituationPage.${key}`, opts);
  const pc = (key, opts) => t(`modules.comptabilite.pages.common.${key}`, opts);
  const dateLocale = getComptabiliteDateLocale(i18n.language);
  const fmtMoney = (value) => formatComptabiliteMoney(value);

  const statutLabelKeys = useMemo(() => ({
    Provisoire: 'status_provisoire',
    Définitif: 'status_definitif',
    Validé: 'status_valide',
    Audité: 'status_audite' }), []);

  const periodLabelKeys = useMemo(() => ({
    Mensuel: 'period_mensuel',
    Trimestriel: 'period_trimestriel',
    Semestriel: 'period_semestriel',
    Annuel: 'period_annuel' }), []);

  const translateStatut = useCallback((val) => (val && statutLabelKeys[val] ? p(statutLabelKeys[val]) : val || p('na')), [p, statutLabelKeys]);
  const translatePeriod = useCallback((val) => (val && periodLabelKeys[val] ? p(periodLabelKeys[val]) : val || p('na')), [p, periodLabelKeys]);

  const [searchTerm, setSearchTerm] = useState('');
  const [resultats, setResultats] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  const [compteResultatSituationFormTab, setCompteResultatSituationFormTab] = useState('resultatInfo');
  const compteResultatSituationFormTabs = useMemo(() => ([
    { id: 'resultatInfo', label: p('section_info_resultat'), icon: ChevronUp },
    { id: 'produitsRevenus', label: p('section_produits_revenus'), icon: ChevronUp },
    { id: 'chargesRatios', label: p('section_ratios_analyse'), icon: ChevronUp }
  ]), [p]);

  
  const [formData, setFormData] = useState({ ...EMPTY_COMPTE_RESULTAT_FORM });
  const [fiscalYearLabel, setFiscalYearLabel] = useState('');

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [setDateRange] = useState({ start: null, end: null });
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_resultats');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statutFilter, setStatutFilter] = useState('all');
  const [isStatutFilterOpen, setIsStatutFilterOpen] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statutFilterRef = useRef(null);

  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const columnFilterRules = useMemo(() => [
    textColumnRule('resultat_ref', (row) => row.resultat_ref ?? ''),
    textColumnRule('exercice_fiscal', (row) => row.exercice_fiscal ?? ''),
    textColumnRule('chiffre_affaires', (row) => String(row.chiffre_affaires ?? '')),
    textColumnRule('resultat_net', (row) => String(row.resultat_net ?? '')),
  ], []);

  useEffect(() => { loadResultats(); }, []);

  useEffect(() => {
    fetchCurrentFiscalYearContext()
      .then(({ label }) => {
        if (label) setFiscalYearLabel(label);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statutFilterRef.current && !statutFilterRef.current.contains(event.target)) setIsStatutFilterOpen(false);    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadResultats = async () => {
    try {
      const response = await getResultats();
      setResultats(sortRecordsNewestFirst(Array.isArray(response) ? response : []));
    } catch (error) {
      console.error('Error loading results:', error);
      setResultats([]);
    }
  };

  const stats = {
    total: resultats.length,
    chiffreAffairesTotal: resultats.reduce((sum, r) => sum + (parseFloat(r.chiffre_affaires) || 0), 0),
    chargesTotales: resultats.reduce((sum, r) => sum + (parseFloat(r.total_charges_exploitation) || 0), 0),
    resultatNetTotal: resultats.reduce((sum, r) => sum + (parseFloat(r.resultat_net) || 0), 0),
    margeBruteMoyenne: resultats.length > 0 ? resultats.reduce((sum, r) => sum + (parseFloat(r.marge_brute) || 0), 0) / resultats.length : 0
  };

  const mainTabs = useMemo(() => [
    { id: 'all_resultats', nameKey: 'tab_overview', icon: LayoutGrid },
    { id: 'par_statut', nameKey: 'tab_by_status', icon: CheckCircle },
    { id: 'par_periode', nameKey: 'tab_by_period', icon: Calendar },
    { id: 'par_performance', nameKey: 'tab_by_performance', icon: TrendingUp }
  ], []);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', nameKey: 'filter_all', icon: Layers },
    { id: 'provisoire', nameKey: 'filter_provisoire', icon: AlertTriangle },
    { id: 'definitif', nameKey: 'filter_definitif', icon: CheckCircle },
    { id: 'valide', nameKey: 'filter_valide', icon: Award }
  ], []);

  const resultatDistribution = useMemo(() => ({
    all_resultats: {
      titleKey: 'dist_overview',
      data: [
        { filterValue: 'Provisoire', nameKey: 'status_provisoire', count: resultats.filter(r => r.statut_resultat === 'Provisoire').length, color: "bg-gradient-to-r from-amber-500 to-amber-600" },
        { filterValue: 'Définitif', nameKey: 'status_definitif', count: resultats.filter(r => r.statut_resultat === 'Définitif').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterValue: 'Validé', nameKey: 'status_valide', count: resultats.filter(r => r.statut_resultat === 'Validé').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterValue: 'Audité', nameKey: 'status_audite', count: resultats.filter(r => r.statut_resultat === 'Audité').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" }
      ]
    },
    par_statut: {
      titleKey: 'dist_by_status',
      data: [
        { filterValue: 'Provisoire', nameKey: 'status_provisoire', count: resultats.filter(r => r.statut_resultat === 'Provisoire').length, color: "bg-gradient-to-r from-amber-500 to-amber-600" },
        { filterValue: 'Définitif', nameKey: 'status_definitif', count: resultats.filter(r => r.statut_resultat === 'Définitif').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterValue: 'Validé', nameKey: 'status_valide', count: resultats.filter(r => r.statut_resultat === 'Validé').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterValue: 'Audité', nameKey: 'status_audite', count: resultats.filter(r => r.statut_resultat === 'Audité').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" }
      ]
    },
    par_periode: {
      titleKey: 'dist_by_period',
      data: [
        { filterValue: 'Mensuel', nameKey: 'period_mensuel', count: resultats.filter(r => r.type_periode === 'Mensuel').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterValue: 'Trimestriel', nameKey: 'period_trimestriel', count: resultats.filter(r => r.type_periode === 'Trimestriel').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterValue: 'Semestriel', nameKey: 'period_semestriel', count: resultats.filter(r => r.type_periode === 'Semestriel').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { filterValue: 'Annuel', nameKey: 'period_annuel', count: resultats.filter(r => r.type_periode === 'Annuel').length, color: "bg-gradient-to-r from-orange-500 to-orange-600" }
      ]
    },
    par_performance: {
      titleKey: 'dist_by_performance',
      data: [
        { filterValue: 'profitable', nameKey: 'performance_profitable', count: resultats.filter(r => (parseFloat(r.resultat_net) || 0) > 0).length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterValue: 'equilibre', nameKey: 'performance_equilibre', count: resultats.filter(r => (parseFloat(r.resultat_net) || 0) === 0).length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterValue: 'deficit', nameKey: 'performance_deficit', count: resultats.filter(r => (parseFloat(r.resultat_net) || 0) < 0).length, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    }
  }), [resultats]);

  const statCards = useMemo(() => [
    { labelKey: 'stat_chiffre_affaires', value: fmtMoney(stats.chiffreAffairesTotal), icon: TrendingUp, color: 'from-emerald-500 to-emerald-700' },
    { labelKey: 'stat_charges_totales', value: fmtMoney(stats.chargesTotales), icon: TrendingDown, color: 'from-red-500 to-red-600' },
    { labelKey: 'stat_resultat_net', value: fmtMoney(stats.resultatNetTotal), icon: Award, color: 'from-green-500 to-green-600' },
    { labelKey: 'stat_marge_brute', value: fmtMoney(stats.margeBruteMoyenne), icon: Percent, color: 'from-amber-500 to-amber-600' },
    { labelKey: 'stat_total_resultats', value: stats.total, icon: FileText, color: 'from-cyan-500 to-cyan-600' }
  ], [stats]);

  const statutSelectOptions = useMemo(() => [
    { value: 'Provisoire', label: p('status_provisoire'), isDefault: true },
    { value: 'Définitif', label: p('status_definitif'), isDefault: true },
    { value: 'Validé', label: p('status_valide'), isDefault: true },
    { value: 'Audité', label: p('status_audite'), isDefault: true }
  ], [p]);

  const periodSelectOptions = useMemo(() => [
    { value: 'Mensuel', label: p('period_mensuel'), isDefault: true },
    { value: 'Trimestriel', label: p('period_trimestriel'), isDefault: true },
    { value: 'Semestriel', label: p('period_semestriel'), isDefault: true },
    { value: 'Annuel', label: p('period_annuel'), isDefault: true }
  ], [p]);

  const distributionInfo = resultatDistribution[mainViewMode] || resultatDistribution.all_resultats;

  const filteredResultats = useMemo(() => {
    const base = resultats.filter(r => {
      if (statutFilter !== 'all' && r.statut_resultat !== statutFilter) return false;

      if (searchTerm && !r.resultat_ref?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.exercice_fiscal?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (mainViewMode === 'all_resultats') {
        if (quickFilter === 'provisoire' && r.statut_resultat !== STATUT_FILTER_VALUES.provisoire) return false;
        if (quickFilter === 'definitif' && r.statut_resultat !== STATUT_FILTER_VALUES.definitif) return false;
        if (quickFilter === 'valide' && r.statut_resultat !== STATUT_FILTER_VALUES.valide) return false;
      }
      if (activeDistributionFilter) {
        switch (mainViewMode) {
          case 'all_resultats':
          case 'par_statut':
            if (r.statut_resultat !== activeDistributionFilter) return false;
            break;
          case 'par_periode':
            if (r.type_periode !== activeDistributionFilter) return false;
            break;
          case 'par_performance':
            if (activeDistributionFilter === 'profitable' && (parseFloat(r.resultat_net) || 0) <= 0) return false;
            if (activeDistributionFilter === 'equilibre' && (parseFloat(r.resultat_net) || 0) !== 0) return false;
            if (activeDistributionFilter === 'deficit' && (parseFloat(r.resultat_net) || 0) >= 0) return false;
            break;
          default:
            break;
        }
      }
      return true;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [resultats, statutFilter, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, columnFilters, columnFilterRules]);

  const {
    selectedRecords: selectedRecords,
    setSelectedRecords: setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectRecordBase,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredResultats, (row) => row.id);
  const handleSelectRecord = handleSelectRecordBase;
  
  

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount } = useComptabiliteTableColumnVisibility(RESULTAT_LIST_COLUMNS, p);
  const resultatTableColSpan = visibleDataColumnCount + 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = buildCompteResultatPayload(formData);
      if (editingRecord) {
        await updateResultat(editingRecord.id, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'حساب النتيجة' }));
      } else {
        await createResultat(submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'حساب النتيجة' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message }));
    } finally {
      resetForm();
      loadResultats();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData(recordToFormData(record));
    setCompteResultatSituationFormTab('resultatInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        await deleteResultat(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'حساب النتيجة' }));
        loadResultats();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => deleteResultat(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'حسابات النتيجة' }));
        setSelectedRecords([]);
        clearSelection();
        loadResultats();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_COMPTE_RESULTAT_FORM });
    setEditingRecord(null);
    setShowModal(false);
    setCompteResultatSituationFormTab('resultatInfo');
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    setFormData({
      ...EMPTY_COMPTE_RESULTAT_FORM,
      exercice_fiscal: fiscalYearLabel || resolveFiscalYearLabel(),
    });
    setCompteResultatSituationFormTab('resultatInfo');
    setShowModal(true);
  };

  const statutOptions = useMemo(() => [
    { value: 'Provisoire', labelKey: 'status_provisoire', icon: AlertTriangle, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { value: 'Définitif', labelKey: 'status_definitif', icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'Validé', labelKey: 'status_valide', icon: Award, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'Audité', labelKey: 'status_audite', icon: AlertCircle, style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' }
  ], []);

  const getStatutBadge = (statut) => {
    const config = statutOptions.find(s => s.value === statut) || statutOptions[0];
    const { labelKey, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{p(labelKey)}</span>;
  };

  const formatYesNo = (val) => {
    if (val === 'Oui') return p('yes');
    if (val === 'Non') return p('no');
    return val || p('no');
  };

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ComptabiliteBulkDeleteFilterAction
              count={selectedRecords.length}
              onClick={handleBulkDelete}
              label={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
              title={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
            />
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
              cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
              tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
            />
          </div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <div className="p-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-700">
              <TrendingUp className="h-8 w-8 text-white force-white" />
            </div>
            {p('title')}
            <button onClick={loadResultats} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{p('advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={openCreateModal}>
            <Plus className="h-5 w-5" /><span>{p('new_resultat')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.slice(0, 4).map((stat, i) => (
          <motion.div key={stat.labelKey} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{p(stat.labelKey)}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_resultats' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(filter.nameKey)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{p(distributionInfo.titleKey)}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.nameKey} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterValue ? null : item.filterValue)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterValue ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterValue ? 'text-white' : 'text-white/80'}`}>{p(item.nameKey)}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterValue ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {isColVisible('resultat_ref') ? (
                    <TableColumnHeaderFilter columnKey="resultat_ref" label={p('col_reference')} value={columnFilters.resultat_ref || ''} onChange={(v) => setColumnFilter('resultat_ref', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('exercice_fiscal') ? (
                    <TableColumnHeaderFilter columnKey="exercice_fiscal" label={p('col_exercice_fiscal')} value={columnFilters.exercice_fiscal || ''} onChange={(v) => setColumnFilter('exercice_fiscal', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('periode') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_period')}</th>
                  ) : null}
                  {isColVisible('chiffre_affaires') ? (
                    <TableColumnHeaderFilter columnKey="chiffre_affaires" label={p('col_chiffre_affaires')} value={columnFilters.chiffre_affaires || ''} onChange={(v) => setColumnFilter('chiffre_affaires', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('resultat_net') ? (
                    <TableColumnHeaderFilter columnKey="resultat_net" label={p('col_resultat_net')} value={columnFilters.resultat_net || ''} onChange={(v) => setColumnFilter('resultat_net', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('statut_resultat') ? (
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('col_status')}</th>
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredResultats.length > 0 ? (
                  filteredResultats.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleSelectRecord(record.id)}
                      />
                      {isColVisible('resultat_ref') ? (
                      <td className="px-6 py-4"><span className="text-white font-semibold">{record.resultat_ref}</span></td>
                      ) : null}
                      {isColVisible('exercice_fiscal') ? (
                      <td className="px-6 py-4"><span className="text-white font-semibold">{record.exercice_fiscal}</span></td>
                      ) : null}
                      {isColVisible('periode') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white/70 text-sm">{translatePeriod(record.type_periode)}</span>
                          <span className="text-white/50 text-xs">{record.periode_debut} → {record.periode_fin}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('chiffre_affaires') ? (
                      <td className="px-6 py-4"><span className="text-white font-bold">{fmtMoney(parseFloat(record.chiffre_affaires) || 0)}</span></td>
                      ) : null}
                      {isColVisible('resultat_net') ? (
                      <td className="px-6 py-4"><span className={`font-bold ${(parseFloat(record.resultat_net) || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmtMoney(parseFloat(record.resultat_net) || 0)}</span></td>
                      ) : null}
                      {isColVisible('statut_resultat') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          {getStatutBadge(record.statut_resultat)}
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                          />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={resultatTableColSpan} className="text-center py-12 text-white/60"><TrendingUp size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_title')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredResultats.length > 0 ? (
              filteredResultats.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><TrendingUp size={20} className="text-emerald-400" />{record.resultat_ref}</h3>
                        <div className="flex">{getStatutBadge(record.statut_resultat)}</div>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_info_generales')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_exercice')}</span> {record.exercice_fiscal}</div>
                            <div><span className="font-medium">{p('card_periode')}</span> {translatePeriod(record.type_periode)}</div>
                            <div><span className="font-medium">{p('card_date_etablissement')}</span> {record.date_etablissement}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_resultats_financiers')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_revenue')}:</span> <span className="text-white font-bold">{fmtMoney(parseFloat(record.chiffre_affaires) || 0)}</span></div>
                            <div><span className="font-medium">{p('label_net_result')}:</span> <span className={`font-bold ${(parseFloat(record.resultat_net) || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmtMoney(parseFloat(record.resultat_net) || 0)}</span></div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_validation')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_daf')}</span> {record.validation_daf ? p('yes') : p('no')}</div>
                            <div><span className="font-medium">{pc(getComplianceFrameworkKey())}:</span> {record.conformite_pcn || p('no')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><TrendingUp size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_title')}</p></div>
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
          onClick={() => setShowViewModal(false)}
          role="presentation"
        >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-emerald-400" />{p('view_info_generales')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-white/60 text-sm mb-1">{p('col_reference')}</p><p className="text-white font-medium">{viewingRecord.resultat_ref}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_exercice_fiscal')}</p><p className="text-white font-medium">{viewingRecord.exercice_fiscal}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('view_type_periode')}</p><p className="text-white font-medium">{translatePeriod(viewingRecord.type_periode)}</p></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('view_resultats_financiers')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('label_revenue')}</p><p className="text-white font-bold text-xl">{fmtMoney(parseFloat(viewingRecord.chiffre_affaires) || 0)}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_net_result')}</p><p className={`font-bold text-xl ${(parseFloat(viewingRecord.resultat_net) || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmtMoney(parseFloat(viewingRecord.resultat_net) || 0)}</p></div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('view_validation')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('label_validation_daf')}</p><p className="text-white font-medium">{viewingRecord.validation_daf || p('no')}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{pc(getComplianceFrameworkKey())}</p><p className="text-white font-medium">{viewingRecord.conformite_pcn || p('no')}</p></div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
        </div>,
        document.body
      )}

      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="resultat-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{editingRecord ? p('btn_update') : p('btn_create')}</button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>
            <div className={`shrink-0 px-6 pt-3 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <div className="flex flex-wrap gap-1" role="tablist">
                {compteResultatSituationFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = compteResultatSituationFormTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setCompteResultatSituationFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? (isDashboardLightMode ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px' : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px') : (isDashboardLightMode ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80' : 'text-white/55 hover:text-white hover:bg-white/5')}`}>
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="resultat-form" onSubmit={handleSubmit}>
                {compteResultatSituationFormTab === 'resultatInfo' && (
                  <div className="space-y-6">
                  <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reference')}</label><input type="text" required value={formData.resultat_ref} onChange={(e) => setFormData({ ...formData, resultat_ref: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_ref')} /></div>
                            <ComptabiliteFiscalYearField label={p('label_exercice_fiscal')} value={formData.exercice_fiscal} onChange={(val) => setFormData({ ...formData, exercice_fiscal: val })} placeholder={p('placeholder_exercice')} required />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_periode_debut')}</label><CustomDatePicker value={formData.periode_debut} onChange={(value) => setFormData({ ...formData, periode_debut: value })} placeholder={p('placeholder_date_debut')} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_periode_fin')}</label><CustomDatePicker value={formData.periode_fin} onChange={(value) => setFormData({ ...formData, periode_fin: value })} placeholder={p('placeholder_date_fin')} /></div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_type_periode')}</label><CustomSelect value={formData.type_periode} onChange={(value) => setFormData({ ...formData, type_periode: value })} options={[
                              { value: 'Mensuel', label: p('period_mensuel'), isDefault: true },
                              { value: 'Trimestriel', label: p('period_trimestriel'), isDefault: true },
                              { value: 'Semestriel', label: p('period_semestriel'), isDefault: true },
                              { value: 'Annuel', label: p('period_annuel'), isDefault: true }
                            ]} searchable={false} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_statut')}</label><CustomSelect value={formData.statut_resultat} onChange={(value) => setFormData({ ...formData, statut_resultat: value })} options={[
                              { value: 'Provisoire', label: p('status_provisoire'), isDefault: true },
                              { value: 'Définitif', label: p('status_definitif'), isDefault: true },
                              { value: 'Validé', label: p('status_valide'), isDefault: true },
                              { value: 'Audité', label: p('status_audite'), isDefault: true }
                            ]} searchable={false} /></div>
                          </div>
                  </div>
                </div>
                )}
                {compteResultatSituationFormTab === 'produitsRevenus' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_chiffre_affaires')}</label><CustomNumberInput step={0.01} value={formData.chiffre_affaires || ''} onChange={(value) => setFormData({ ...formData, chiffre_affaires: value })} placeholder="0.00" /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_total_produits')}</label><CustomNumberInput step={0.01} value={formData.total_produits_exploitation || ''} onChange={(value) => setFormData({ ...formData, total_produits_exploitation: value })} placeholder="0.00" /></div>
                          </div>
                )}
                {compteResultatSituationFormTab === 'chargesRatios' && (
                  <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_total_charges')}</label><CustomNumberInput step={0.01} value={formData.total_charges_exploitation || ''} onChange={(value) => setFormData({ ...formData, total_charges_exploitation: value })} placeholder="0.00" /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{pc(getIncomeTaxLineKey())}</label><CustomNumberInput step={0.01} value={formData.impots_benefices || ''} onChange={(value) => setFormData({ ...formData, impots_benefices: value })} placeholder="0.00" /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{pc('tax_vat')}</label><CustomNumberInput step={0.01} value={formData.impots_taxes || ''} onChange={(value) => setFormData({ ...formData, impots_taxes: value })} placeholder="0.00" /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_net_result')}</label><CustomNumberInput step={0.01} value={formData.resultat_net || ''} onChange={(value) => setFormData({ ...formData, resultat_net: value })} placeholder="0.00" /></div>
                          </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_marge_brute')}</label><CustomNumberInput step={0.01} value={formData.marge_brute || ''} onChange={(value) => setFormData({ ...formData, marge_brute: value })} placeholder="0.00" /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_ebitda')}</label><CustomNumberInput step={0.01} value={formData.ebitda || ''} onChange={(value) => setFormData({ ...formData, ebitda: value })} placeholder="0.00" /></div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_notes')} /></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default CompteResultatSituationPage;

